import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Row,
  Col,
  Form,
  Input,
  Radio,
  Select,
  DatePicker,
  Button,
  Modal,
  Checkbox,
  Switch,
  Tooltip,
  Alert,
  message,
} from 'antd'
import { Link, useHistory, useLocation } from 'react-router-dom'
import '../../css/patient.add.scss'
import util from '../../util/util'
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input/input'
import 'react-phone-number-input/style.css'
import {
  setNewPatient,
  validatePatient,
  editPatient,
} from '../../store/action/patient.action'
import moment from 'moment'
import { constants } from '../../constants'
import {
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { getHelp, pages } from '../../tutorials'
import ApolloClientService from '../../services/apolloclient.service'
import PatientService from '../../services/patient.service'

const apolloService = new ApolloClientService()
const patientService = new PatientService()

const PatientAddForm = (props) => {
  var history = useHistory()
  var location = useLocation()
  const [isStateAvailable, setIsStateAvailble] = useState(true)
  const [regimEdit, setRegimEdit] = useState(false)
  const [form] = Form.useForm()
  const [registerPatientError, setRegisterPatientError] = useState(false)
  const [surgeryTypes, setSurgeryTypes] = useState([])
  const [selectedSurgeryType, setSelectedSurgeryType] = useState(null)
  const [patientPhoneWarning, setPatientPhoneWarning] = useState(false)
  // Raised when the clinician picks another language; shows the "next login"
  // note as a closable alert, not only behind the (i) icon.
  const [languageChanged, setLanguageChanged] = useState(false)
  const [caregiverPhoneWarning, setCaregiverPhoneWarning] = useState(false)
  const [consentDownloading, setConsentDownloading] = useState(false)
  // The language list comes from the SERVER (supportedLanguages -> the API's
  // config/languages.js), not a copy here. The admin already reads it this way
  // in the Reminders manager; a second hardcoded list is how two parts of one
  // app end up disagreeing about which languages exist.
  // Re-read the patient on mount. getPatient is already fetchPolicy:'no-cache',
  // so this is always the server's current copy. Registration has no patient to
  // fetch, and a half-built one must not be overwritten.
  useEffect(() => {
    const id = navPatient && navPatient._id
    if (!id || props.isRegistration) return
    let cancelled = false
    patientService
      .getPatient(id)
      .then((res) => {
        const doc = res && res.data ? res.data : res
        if (!cancelled && doc && doc._id) setFetchedPatient(doc)
      })
      .catch(() => {
        // Keep showing the navigation-state copy. Stale beats blank, and the
        // clinician can still see who they are looking at.
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [languages, setLanguages] = useState([])
  useEffect(() => {
    new ApolloClientService()
      .supportedLanguages()
      .then((list) => setLanguages(list || []))
      .catch(() => {
        // Fall back to English only. The field still renders and still saves;
        // it just cannot offer a language we failed to look up.
        setLanguages([{ code: 'en', label: 'English', nativeLabel: 'English' }])
      })
  }, [])

  // The patient shown by this form.
  //
  // It used to come ONLY from location.state — the object handed over by
  // whichever page linked here. That object is a snapshot of whatever the
  // LIST held when the list was loaded, so anything changed since (by the
  // patient in the app, or by another clinician) was invisible here, and
  // stayed invisible through a page reload, because reloading re-reads the
  // same navigation state. Found 2026-10-06: a patient switched to Spanish,
  // the database and the app agreed immediately, and this form said EN until
  // the admin was logged out and back in. Language is only what made it
  // visible — the same staleness covered phone, surgery date and caregiver,
  // any of which a clinician might act on.
  //
  // Navigation state is now a FIRST-PAINT HINT only: it renders instantly, and
  // the fetch below replaces it with the server's copy.
  const navPatient = location.state?.patient
  const [fetchedPatient, setFetchedPatient] = useState(null)
  var patient = fetchedPatient || navPatient
  patient = props.isRegistration && props.patient ? props.patient : patient

  // Fetch the signed consent PDF(s). A patient signs one per consent form, so
  // ask what exists first: one document downloads straight away, several are
  // listed so the clinician picks. Replaces the PDF that used to be emailed to
  // support — see docs/PLAN_MINIMIZE_PHI_IN_EMAIL.md.
  const downloadConsent = async () => {
    if (!patient?._id) return
    setConsentDownloading(true)
    try {
      const list = await patientService.listConsentDocuments(patient._id)
      const documents = list?.documents || []
      if (documents.length === 0) {
        message.info('No signed consent is stored for this patient.')
      } else if (documents.length === 1) {
        await patientService.downloadConsent(
          patient._id,
          documents[0].index,
          documents[0].filename
        )
      } else {
        // Downloading several files at once is blocked by most browsers, so
        // let the clinician choose rather than silently delivering one of them.
        Modal.info({
          title: 'Signed consent documents',
          content: (
            <div>
              {documents.map((doc) => (
                <p key={doc.index}>
                  <a
                    onClick={() =>
                      patientService
                        .downloadConsent(patient._id, doc.index, doc.filename)
                        .catch(() =>
                          message.error('Could not download that document.')
                        )
                    }
                  >
                    {doc.filename}
                  </a>
                </p>
              ))}
            </div>
          ),
        })
      }
    } catch (err) {
      const status = err?.response?.status
      const reason = err?.response?.data?.reason
      // The server distinguishes "you are outside this patient's clinic" from
      // "this patient is retired, which narrows access to company admins". The
      // second is a rule the user can act on; a generic failure just gets retried.
      if (status === 403 && reason === 'retired_patient')
        message.warning(
          'This patient is inactive. Their signed consent is retained, but only a company administrator can download it.'
        )
      else if (status === 403)
        message.error('You do not have access to this patient’s consent.')
      else if (status === 404)
        message.info('No signed consent is stored for this patient.')
      else message.error('Could not download the consent form.')
    } finally {
      setConsentDownloading(false)
    }
  }

  useEffect(() => {
    var patient = location.state?.patient
    patient && props.setNewPatient(patient)
    getHelp(pages.ADDPATIENT)
  }, [])

  useEffect(() => {
    fetchSurgeryTypes()
  }, [props.customer, patient])

  useEffect(() => {
    // For new patients with non-surgical discipline type, default date to current date
    if (!patient?._id && selectedSurgeryType && !selectedSurgeryType.issurgical) {
      const currentDate = form.getFieldValue('date')
      if (!currentDate) {
        form.setFieldsValue({ date: moment() })
      }
    }
  }, [selectedSurgeryType, patient])

  var fetchSurgeryTypes = async () => {
    try {
      // Use patient's customer when editing, otherwise use logged-in user's customer
      const customerToUse = patient?.customer || props.customer

      if (customerToUse?.surgerytypes && customerToUse.surgerytypes.length > 0) {
        setSurgeryTypes(customerToUse.surgerytypes)
        // Set initial surgery type if editing
        if (patient?.surgery?.type) {
          const initialType = customerToUse.surgerytypes.find(
            st => st.name.toLowerCase() === patient.surgery.type.toLowerCase()
          )
          setSelectedSurgeryType(initialType)
        } else if (customerToUse.surgerytypes.length > 0) {
          // Set to first available type for new patients
          const firstType = customerToUse.surgerytypes[0]
          setSelectedSurgeryType(firstType)
          // Update form field with the first surgery type
          form.setFieldsValue({ type: firstType.name.toLowerCase() })
        }
      } else {
        setSurgeryTypes([])
      }
    } catch (error) {
      console.error('Error fetching surgery types:', error)
      setSurgeryTypes([])
    }
  }

  const isSurgicalType = () => {
    if (!selectedSurgeryType) return true // Default to surgical if unknown
    return selectedSurgeryType.issurgical
  }

  const handleSurgeryTypeChange = (value) => {
    const selectedType = surgeryTypes.find(
      st => st.name.toLowerCase() === value.toLowerCase()
    )
    setSelectedSurgeryType(selectedType)

    // If switching to non-surgical and no date is set, default to today
    if (selectedType && !selectedType.issurgical) {
      const currentDate = form.getFieldValue('date')
      if (!currentDate) {
        form.setFieldsValue({ date: moment() })
      }
    }
  }

  const dateFormatList = [
    'MM/DD/YYYY',
    'MM/DD/YY',
    'MM-DD-YYYY',
    'MM-DD-YY',
    'MMDDYYYY',
    'MMDDYY',
  ]

  var renderRelationshipOptions = () => {
    var RelationshipOptions = [
      { name: 'Spouse or Partner' },
      { name: 'Child' },
      { name: 'Sibling' },
      { name: 'Friend' },
      { name: 'Parent' },
      { name: 'Professional caregiver' },
      { name: 'Other' },
    ]
    return RelationshipOptions.map((Relation) => (
      <Select.Option value={Relation.name}>{Relation.name}</Select.Option>
    ))
  }

  var renderHeightOptions = () => {
    var startHeight = 48
    var endHeight = 109
    var difference = endHeight - startHeight

    var HeightOptions = []
    for (var i = 0; i < difference; i++) {
      var currentHeight = startHeight + i
      var remainder = currentHeight % 12
      var numberOfFeet = (currentHeight - remainder) / 12

      var feetString = numberOfFeet + "'" + remainder + '"'
      HeightOptions.push({ name: feetString, value: currentHeight })
    }

    return HeightOptions.map((ht) => (
      <Select.Option value={ht.value}>{ht.name}</Select.Option>
    ))
  }

  var renderSurgeonsOptions = () => {
    if (!location.state?.patient?._id && !patient)
      if (props.surgeons && props.surgeons.length == 1)
        form.setFieldsValue({ surgeon: props.surgeons[0]._id })
    return props.surgeons
      ? props.surgeons.map((surgeon) => (
          <Select.Option value={surgeon._id}>{surgeon.name}</Select.Option>
        ))
      : null
  }

  var getAgrGroup = (date) => {
    var age = moment().diff(moment(date.toDate()), 'years')
    for (let i = 0; i < constants.AGE_GROUP.length; i++) {
      if (constants.AGE_GROUP[i][0] <= age && constants.AGE_GROUP[i][1] >= age)
        return constants.AGE_GROUP[i][2]
    }
    return constants.AGE_GROUP[constants.AGE_GROUP.length - 1]
  }

  var showConfirmBox = (value) => {
    if (!util.isSame(patient?.surgery?.date, value.date.toDate())) {
      Modal.confirm({
        title:
          'Do you want to reschedule regimen (Exercise and Measurements) with surgery date?',
        icon: <ExclamationCircleFilled />,
        content: '',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => handleFormSubmit({ ...value, regimEdit: true }),
        onCancel: () => handleFormSubmit(value),
      })
    } else handleFormSubmit(value)
  }
  const onFinishFailed = ({ errorFields }) => {
    if (!props.isRegistration) return
    setRegisterPatientError(true)
    setTimeout(() => {
      setRegisterPatientError(false)
    }, 5000)
  }
  var handleFormSubmit = async (value) => {
    var surgery = props.isRegistration
      ? {}
      : { type: value.type, date: value.date.toDate() }
    var shipaddress = {
      addressline1: value.addressline1,
      addressline2: value.addressline2,
      city: value.city,
      state: isStateAvailable ? value.state : '',
      zip: value.zip,
      country: value.country,
    }
    var caregiver = {
      name: value.careGiverFName,
      fname: value.careGiverFName,
      lname: value.careGiverLName,
      relationship: value.careGiverRelationship,
      email: value.careGiverEmail,
      phone: value.careGiverPhone,
    }
    delete value.type
    delete value.date
    var patObj = {
      // Written through editPatient like every other field here. `input
      // Patientin` already carries `language`, and editPatient does
      // Object.assign(patObj, {...input}), so no server change was needed.
      language: value.language || 'en',
      firstname: value.firstname,
      lastname: value.lastname,
      age: getAgrGroup(value.dob),
      gender: value.gender,
      consent: value.consent,
      registered: value.registered,
      height: value.height,
      dob: value.dob.format('MM/DD/YYYY'),
      email: value.email,
      surgery,
      customer: patient?.customer?._id || props.customer?._id,
      phone: parseInt(value.phone),
      shipaddress,
      caregiver,
      surgeon: value.surgeon,
      regimEdit: value.regimEdit || false,
      regid: patient?.regid || '',
      email_opt_in:value.email_opt_in,
      // ⚠️ This object is built FIELD BY FIELD, so a new form control is not
      // sent unless it is added here. The switch will move, the form will
      // submit, the mutation will succeed -- and the value is simply never
      // transmitted. Cost one prod deploy cycle on 2026-09-23, and the server
      // side of the same trap (business/customer.js updateCustomer) cost
      // another the day before.
      //
      // `=== true` rather than the raw value: an untouched Switch on a record
      // predating the field yields undefined, and sending undefined would
      // leave the document's own default in place rather than writing false.
      istestpatient: value.istestpatient === true
    }

    if (props.isRegistration) {
      props.setNewRegistrationPatient({
        ...patObj,
        lastdigitsofphone: patObj.phone.toString().slice(-4),
      })
      return
    }

    if (!patient?._id) if (!(await props.validatePatient(patObj))) return
    if (patient?._id) {
      if ((await props.editPatient(patObj, patient?._id)) == undefined) return
      await props.setNewPatient({
        ...patObj,
        lastdigitsofphone: patObj.phone.toString().slice(-4),
      })
      history.push({
        pathname: `/patient/${patient?._id}`,
        state: {
          patient: patObj,
          lastdigitsofphone: patObj.phone.toString().slice(-4),
        },
      })
    } else {
      await props.setNewPatient(patObj)
      history.push({
        pathname: '/patient/add/careplan',
        state: { patient: patObj },
      })
    }
  }

  return (
    <>
      {!props.isRegistration && (
        <Row align='middle' className='headerRow'>
          <Col className='headerText'>
            {patient?._id ? 'EDIT PATIENT ' : 'ADD PATIENT'}
          </Col>
        </Row>
      )}
      <Col>
        <Form
          /* initialValues are read ONCE, at mount. Without this key the fetched
             patient would land in state and change nothing on screen — the very
             bug being fixed. Keying on it remounts the form once, as the server
             copy arrives and before anyone has had time to type. */
          key={fetchedPatient ? 'server' : 'nav'}
          form={form}
          layout={'vertical'}
          onFinish={
            location.state?.patient?._id ? showConfirmBox : handleFormSubmit
          }
          onFinishFailed={onFinishFailed}
          onValuesChange={(changedValues) => {
            if ('phone' in changedValues)
              setPatientPhoneWarning(changedValues.phone?.startsWith('+1555') || false)
            if ('careGiverPhone' in changedValues)
              setCaregiverPhoneWarning(changedValues.careGiverPhone?.startsWith('+1555') || false)
          }}
          initialValues={{
            patient,
            firstname: patient?.firstname ? patient?.firstname : '',
            lastname: patient?.lastname ? patient?.lastname : '',
            gender: patient?.gender ? patient?.gender : '',
            consent: patient?.consent ? patient?.consent : false,
            registered: patient?.registered ? patient?.registered : false,
            istestpatient: patient?.istestpatient === true,
            // 🔴 NOT `x ? x : true` -- that pattern renders a patient who has
            // opted OUT as opted in, because `false ? false : true` is true.
            // The checkbox then showed "Allow Email Alerts" ticked for all 68
            // accounts the opt-out script had just silenced (staging,
            // 2026-09-24), and saving the form for any unrelated reason sent
            // that `true` straight back to the server and re-subscribed them.
            //
            // `!== false` matches the server's own rule: reminderengine.js
            // treats `email_opt_in !== false` as opted in, so an ABSENT field
            // (records predating it) is opted in and an explicit false is not.
            email_opt_in: patient?.email_opt_in !== false,
            height: patient?.height ? parseInt(patient?.height) : '',
            phone: patient?.phone ? '+' + patient?.phone + '' : '',
            dob: patient?.dob ? moment(patient?.dob) : null,
            email: patient?.email ? patient.email : '',
            surgeon: patient?.surgeon
              ? patient?.surgeon?._id
                ? patient?.surgeon?._id
                : patient?.surgeon
              : '',
            type: patient?.surgery?.type || 'knee',
            date: patient?.surgery?.date
              ? moment(patient?.surgery?.date)
              : patient?.surgery?.date,
            addressline1: patient?.shipaddress?.addressline1
              ? patient?.shipaddress?.addressline1
              : '',
            addressline2: patient?.shipaddress?.addressline2
              ? patient?.shipaddress?.addressline2
              : '',
            city: patient?.shipaddress?.city ? patient?.shipaddress?.city : '',
            state: patient?.shipaddress?.state
              ? patient?.shipaddress?.state
              : '',
            zip: patient?.shipaddress?.zip
              ? (patient?.shipaddress?.zip).toString()
              : '',
            country: patient?.shipaddress?.country
              ? patient?.shipaddress?.country
              : 'US',
            careGiverName: patient?.caregiver?.name
              ? patient?.caregiver?.name
              : patient?.caregiver?.fname,
            careGiverFName: patient?.caregiver?.fname
              ? patient?.caregiver?.fname
              : patient?.caregiver?.name,
            careGiverLName: patient?.caregiver?.lname
              ? patient?.caregiver?.lname
              : '',
            careGiverRelationship: patient?.caregiver?.relationship
              ? patient?.caregiver?.relationship
              : 'Other',
            careGiverEmail: patient?.caregiver?.email
              ? patient?.caregiver?.email
              : '',
            careGiverPhone: patient?.caregiver?.phone
              ? patient?.caregiver?.phone[0] == '+'
                ? patient?.caregiver?.phone
                : '+' + patient?.caregiver?.phone + ''
              : '',
          }}
        >
          <Row gutter={50}>
            <Col
              id={'personaldetailsform'}
              md={12}
              xs={24}
              gutter={25}
              style={{ marginBottom: 10 }}
            >
              <fieldset className='fieldsetContainer'>
                <legend className='fieldsetHeader'>PERSONAL DETAILS</legend>
                <Row gutter={25}>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='FIRST NAME'
                      name='firstname'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please enter patient first name.',
                        },
                      ]}
                    >
                      <Input maxLength={40} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='LAST NAME'
                      name='lastname'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please enter patient last name.',
                        },
                      ]}
                    >
                      <Input maxLength={40} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='DATE OF BIRTH'
                      name='dob'
                      rules={[
                        {
                          required: true,
                          message: 'Please enter patient birth date.',
                        },
                      ]}
                    >
                      <DatePicker
                        suffixIcon={null}
                        allowClear={false}
                        placeholder='MM/DD/YYYY'
                        format={dateFormatList}
                        showToday={false}
                        disabledDate={(current) => {
                          return current >= moment().add(-1, 'days')
                        }}
                        style={{ width: '100%' }}
                        defaultPickerValue={moment().add(-50, 'years')}
                      />
                    </Form.Item>
                  </Col>
                  {/* <Col md={12} xs={24}>
                    <Form.Item
                      hidden="true"
                      label="HEIGHT"
                      name="height"
                      rules={[
                        {
                          required: true,
                          message: "Please select patient height.",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {renderHeightOptions()}
                      </Select>
                      </Form.Item>
                  </Col> */}
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='CELL PHONE'
                      name='phone'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please enter patient cell number.',
                        },
                        () => ({
                          validator(rule, value) {
                            var number = value
                              ? value.charAt(0) == '+'
                                ? value
                                : '+1' + value
                              : ''
                            if (!value || isPossiblePhoneNumber(number)) {
                              return Promise.resolve()
                            }
                            return Promise.reject('Please enter a valid number.')
                          },
                        }),
                      ]}
                    >
                      <PhoneInput
                        addInternationalOption={false}
                        defaultCountry='US'
                        className='phone-input fullWidth'
                        enableAreaCodes
                      />
                    </Form.Item>
                    {patientPhoneWarning && (
                      <div style={{ color: '#faad14', fontSize: 12, marginTop: -16, marginBottom: 8 }}>
                        Note: SMS messages will not be sent to 555 numbers.
                      </div>
                    )}
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='EMAIL'
                      name='email'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please enter valid email',
                          type: 'email',
                        },
                      ]}
                    >
                      <Input maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='GENDER'
                      name='gender'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please select patient gender.',
                        },
                      ]}
                    >
                      <Radio.Group>
                        <Radio value='male'>MALE</Radio>
                        <Radio value='female'>FEMALE</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    {/* Marks a record created for testing -- a provider
                        evaluating the platform, or staff enrolling to see what
                        patients see. Shows a TEST badge in the patient list and
                        the detail header.

                        Display only. It does NOT exclude the patient from
                        counts, dashboards or RPM billing, and it deliberately
                        does NOT unlock the delete guard the way a test CLINIC
                        does -- see data/models/patient.js and
                        docs/DESIGN_TEST_PATIENT_TAG.md. */}
                    {/* Label and control on ONE line. The form is
                        layout='vertical', which stacks every label above its
                        control, so this cannot be done with Form.Item's own
                        label -- `noStyle` drops the wrapper and lets the Switch
                        sit inside a flex row we control. */}
                    <Form.Item
                      label={<span style={{ visibility: 'hidden' }}>.</span>}
                      colon={false}
                      style={{ marginBottom: 12 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ whiteSpace: 'nowrap' }}>TEST PATIENT</span>
                        <Form.Item name='istestpatient' valuePropName='checked' noStyle>
                          <Switch checkedChildren='Test' unCheckedChildren='Real' />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </Col>
                  {!props.isRegistration && (
                    <Col md={12} xs={24}>
                      {/* Checkbox and link share one flex row so the link sits
                          beside "Consent" rather than under it. Form.Item is a
                          block element with its own bottom margin, so the two
                          cannot simply be siblings. The margin moves to the
                          wrapper, keeping this column the same height as the
                          "Registered" one next to it. */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '24px',
                        }}
                      >
                        <Form.Item
                          valuePropName='checked'
                          label={null}
                          name='consent'
                          style={{ marginBottom: 0 }}
                        >
                          <Checkbox disabled={form.getFieldValue('consent') || true}>
                            Consent
                          </Checkbox>
                        </Form.Item>
                        {/* Only offered once consent is actually signed — there
                            is nothing to download before that. */}
                        {patient?.consent && patient?._id && (
                          <a
                            onClick={
                              consentDownloading ? undefined : downloadConsent
                            }
                            style={{
                              marginLeft: '16px',
                              whiteSpace: 'nowrap',
                              // No <Spin> wrapper: it renders a block container
                              // and would push the link back onto its own line.
                              opacity: consentDownloading ? 0.6 : 1,
                              cursor: consentDownloading ? 'default' : 'pointer',
                            }}
                          >
                            {consentDownloading
                              ? 'Downloading…'
                              : 'Download Consent'}
                          </a>
                        )}
                      </div>
                    </Col>
                  )}
                  {!props.isRegistration && (
                    <Col md={12} xs={24}>
                      <Form.Item
                        valuePropName='checked'
                        label={null}
                        name='registered'
                        disabled={'registered'}
                      >
                        <Checkbox disabled={form.getFieldValue('registered') || true}>
                          Registered
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  )}


                    <Col md={12} xs={24}>
                      {/* nowrap, not wrap: this sits in a half-width Col, and
                          "Allow Email Alerts" + the language is a few px over
                          at common widths, so it wrapped to a second line every
                          time. The label is "Lang:" for the same reason — the
                          badge already says EN/ES, so the word adds width
                          without adding meaning. */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          flexWrap: 'nowrap',
                          columnGap: 12,
                        }}
                      >
                        <Form.Item
                          valuePropName='checked'
                          label={null}
                          name='email_opt_in'
                          style={{ marginBottom: 0 }}
                        >
                          <Checkbox>Allow Email Alerts</Checkbox>
                        </Form.Item>
                        {/* Editable, deliberately -- it was read-only when the
                            badge was added, on the reasoning that the patient
                            owns this setting and an operator should not
                            silently override it. In practice that left the one
                            person who cannot read the English UI as the only
                            person able to change it away from English: a
                            patient who phones to ask for Spanish had to be
                            talked through Settings in a language they do not
                            read. The clinician needs this.

                            It writes through editPatient like every other field
                            on this form, so it lands in patient.language and the
                            app picks it up on next load -- the same column the
                            patient's own Settings toggle writes. */}
                        <span
                          style={{
                            whiteSpace: 'nowrap',
                            color: 'rgba(0, 0, 0, 0.65)',
                            display: 'inline-flex',
                            alignItems: 'baseline',
                            gap: 6,
                          }}
                        >
                          Lang:
                          <Form.Item
                            name='language'
                            noStyle
                            initialValue={patient?.language === 'es' ? 'es' : 'en'}
                          >
                            <Select
                              size='small'
                              style={{ width: 108 }}
                              onChange={() => setLanguageChanged(true)}
                            >
                              {languages.map((l) => (
                                <Select.Option key={l.code} value={l.code}>
                                  {l.nativeLabel || l.label || l.code}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          {/* Saving here changes patient.language immediately,
                              but an app ALREADY OPEN will not switch: the
                              webapp resolves its text from the language it read
                              when the profile loaded, and an open session never
                              re-reads it. The patient sees the change when they
                              next open or resume the app. Without this note a
                              clinician watching the patient's screen concludes
                              the save failed and tries again. */}
                          <Tooltip title='Takes effect after the patient next logs in.'>
                            <InfoCircleOutlined
                              style={{
                                color: 'rgba(0, 0, 0, 0.45)',
                                cursor: 'help',
                              }}
                            />
                          </Tooltip>
                        </span>
                      </div>
                      {/* The tooltip only shows on hover, and a clinician who
                          just changed the language and is watching the
                          patient's screen does not hover. Shown on change,
                          dismissable. */}
                      {languageChanged && (
                        <Alert
                          type='info'
                          showIcon
                          closable
                          onClose={() => setLanguageChanged(false)}
                          message='Language change takes effect after the patient next logs in.'
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </Col>
                 
                    
                  
                </Row>
              </fieldset>
            </Col>
            <Col
              id={'addressform'}
              md={12}
              xs={24}
              gutter={25}
              style={{ marginBottom: 10 }}
            >
              <fieldset className='fieldsetContainer'>
                <legend className='fieldsetHeader'>SHIP ADDRESS</legend>

                <Row gutter={25}>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='ADDRESS LINE 1'
                      name='addressline1'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please enter address.',
                        },
                      ]}
                    >
                      <Input maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='ADDRESS LINE 2'
                      name='addressline2'
                      rules={[
                        {
                          required: false,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='CITY'
                      name='city'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please select city name.',
                        },
                      ]}
                    >
                      <Input maxLength={20} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    {isStateAvailable && (
                      <Form.Item
                        label='STATE'
                        name='state'
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: 'Please select state.',
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          defaultValue={'Alabama'}
                        >
                          {constants.USA_STATE.map((s) => {
                            return <Select.Option value={s}>{s}</Select.Option>
                          })}
                        </Select>
                      </Form.Item>
                    )}
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='ZIP CODE'
                      name='zip'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please enter zip code',
                        },
                      ]}
                    >
                      <Input maxLength={20} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='COUNTRY'
                      name='country'
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: 'Please select country.',
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        defaultValue={'US'}
                        onChange={(e) => {
                          e == 'US'
                            ? setIsStateAvailble(true)
                            : setIsStateAvailble(false)
                        }}
                      >
                        {constants.COUNTRYS.map((c) => {
                          return (
                            <Select.Option value={c.code}>
                              {c.name}
                            </Select.Option>
                          )
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </fieldset>
            </Col>
            {!props.isRegistration && (
              <Col
                id={'surgerydetailsform'}
                md={12}
                xs={24}
                gutter={25}
                style={{ marginBottom: 10 }}
              >
                <fieldset className='fieldsetContainer'>
                  <legend className='fieldsetHeader'>SURGERY DETAILS</legend>
                  <Row gutter={25}>
                    <Col md={12} xs={24}>
                      <Row>
                        <Col md={24} xs={24}>
                          <Form.Item
                            label='SURGERY TYPE'
                            name='type'
                            rules={[
                              {
                                required: true,
                                whitespace: true,
                                message: 'Please select surgery type.',
                              },
                            ]}
                          >
                            <Select
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              defaultValue={surgeryTypes.length > 0 ? surgeryTypes[0].name.toLowerCase() : 'knee'}
                              onChange={handleSurgeryTypeChange}
                            >
                              {surgeryTypes.map((surgery) => (
                                <Select.Option key={surgery._id} value={surgery.name.toLowerCase()}>
                                  {surgery.description}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col md={24} xs={24}>
                          <Form.Item
                            label='PROVIDER'
                            name='surgeon'
                            rules={[
                              {
                                required: true,
                                whitespace: true,
                                message: 'Please select a provider.',
                              },
                            ]}
                          >
                            <Select
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              // disabled={location.state?.patient?._id}
                            >
                              {renderSurgeonsOptions()}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col md={24} xs={24}>
                          <Form.Item
                            label={isSurgicalType() ? 'DATE OF SURGERY' : 'START DATE'}
                            name='date'
                            rules={[
                              {
                                required: true,
                                message: isSurgicalType() ? 'Please select surgery date.' : 'Please select start date.',
                              },
                            ]}
                          >
                            <DatePicker
                              suffixIcon={null}
                              allowClear={false}
                              placeholder='MM/DD/YYYY'
                              format={dateFormatList}
                              showToday={true}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </fieldset>
              </Col>
            )}
            <Col
              id={'caregiverform'}
              md={12}
              xs={24}
              style={{ marginBottom: 10 }}
            >
              <fieldset className='fieldsetContainer'>
                <legend className='fieldsetHeader'>CAREGIVER DETAILS</legend>

                <Row gutter={25}>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='FIRST NAME'
                      name='careGiverFName'
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message: 'Please enter caregiver first name.',
                        },
                      ]}
                    >
                      <Input maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='LAST NAME'
                      name='careGiverLName'
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message: 'Please enter caregiver last name.',
                        },
                      ]}
                    >
                      <Input maxLength={20} />
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item
                      label='CELL PHONE'
                      name='careGiverPhone'
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message: 'Please enter caregiver cell number',
                        },
                        () => ({
                          validator(rule, value) {
                            var number = value
                              ? value.charAt(0) == '+'
                                ? value
                                : '+' + value
                              : ''
                            if (!value || isPossiblePhoneNumber(number)) {
                              return Promise.resolve()
                            }
                            return Promise.reject('Please enter a valid number.')
                          },
                        }),
                      ]}
                    >
                      <PhoneInput
                        addInternationalOption={false}
                        defaultCountry='US'
                        className='phone-input fullWidth'
                        enableAreaCodes
                      />
                    </Form.Item>
                    {caregiverPhoneWarning && (
                      <div style={{ color: '#faad14', fontSize: 12, marginTop: -16, marginBottom: 8 }}>
                        Note: SMS messages will not be sent to 555 numbers.
                      </div>
                    )}
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='EMAIL'
                      name='careGiverEmail'
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message: 'Please enter valid caregiver email',
                          type: 'email',
                        },
                      ]}
                    >
                      <Input maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label='RELATIONSHIP'
                      name='careGiverRelationship'
                      rules={[
                        {
                          required: false,
                          whitespace: true,
                          message:
                            'Please select a Relationship with caregiver.',
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        defaultValue='Other'
                      >
                        {renderRelationshipOptions()}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </fieldset>
            </Col>
          </Row>
          <Row>&nbsp;</Row>
          {props.isRegistration && (
            <Row justify='end' style={{ color: 'red', minHeight: '1em' }}>
              {registerPatientError
                ? 'There is an error in the form. Please check above.'
                : ''}
            </Row>
          )}
          <Row justify='end'>
            <Col md={3} xs={12}>
              <Button type='primary' className='submit-form-button'>
                {/* <Link to={location.state?.home ? "/home" : "/patient"}> */}
                <Link
                  to={
                    location.state?.patient?._id
                      ? `/patient/${patient?._id}`
                      : '/patient'
                  }
                >
                  Cancel
                </Link>
              </Button>
            </Col>
            <Col md={3} xs={12}>
              <Button
                id={'nextstep'}
                style={{ marginLeft: 20 }}
                type='primary'
                htmlType='submit'
                className='submit-form-button'
              >
                {location.state?.patient?._id ? 'Save' : 'Next'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Col>
    </>
  )
}

PatientAddForm.propTypes = {
  isLoading: PropTypes.bool,
  token: PropTypes.string,
  customer: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    token: util.verifyToken(state.profile.token),
    isLoading: state.profile.isLoading,
    customer: state.profile.customer,
    surgeons: state.surgeon.surgeons,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    validatePatient: (patient) => dispatch(validatePatient(patient)),
    setNewPatient: (patient) => dispatch(setNewPatient(patient)),
    editPatient: (patient, id) => dispatch(editPatient(patient, id)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientAddForm)
